import React from 'react';
import { Link } from 'react-router-dom';
import { 
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  HeartIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  CalendarIcon,
  TrophyIcon,
  AcademicCapIcon,
  ClockIcon,
  HomeIcon
} from '@heroicons/react/24/outline';
import { clubInfo } from '../data/content';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Startseite', path: '/' },
    { name: 'Sportarten', path: '/trainings' },
    { name: 'Über uns', path: '/about' },
    { name: 'Termine', path: '/termine' },
    { name: 'Kontakt', path: '/kontakt' }
  ];

  const sportLinks = [
    { name: 'Unterordnung', path: '/unterordnung' },
    { name: 'Rally Obedience', path: '/sport/rally-obedience' },
    { name: 'Agility', path: '/sport/agility' },
    { name: 'Turnierhundesport', path: '/sport/turnierhundesport' }
  ];

  const legalLinks = [
    { name: 'Impressum', path: '/impressum', icon: DocumentTextIcon },
    { name: 'Datenschutz', path: '/datenschutz', icon: ShieldCheckIcon },
    { name: 'Platzordnung', path: '/platzordnung', icon: DocumentTextIcon },
    { name: 'Vereinsgeschichte', path: '/geschichte', icon: ClockIcon }
  ];


  return (
    <footer className="bg-surface-light dark:bg-surface-dark border-t border-border-light dark:border-border-dark">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-8 md:py-12">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-12 gap-6 md:gap-8">
          
          {/* Brand Column */}
          <div className="space-y-4 col-span-2 lg:col-span-4">
            <div className="flex items-center gap-3">
              <img 
                src={clubInfo.logo}
                alt="HSV Adlershof Logo"
                className="w-12 h-12 rounded-xl shadow-sm"
              />
              <div>
                <h3 className="font-bold text-primary-600 dark:text-primary-400">
                  {clubInfo.name}
                </h3>
                <p className="text-xs text-neutral-600 dark:text-neutral-400">
                  Gegründet {clubInfo.founded} • DVG-Mitglied
                </p>
              </div>
            </div>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
              Seit über 75 Jahren fördern wir die Harmonie zwischen Mensch und Hund durch 
              sportliches Training und Gemeinschaft im Herzen von Berlin-Adlershof.
            </p>
            
            {/* Social Links / Badges */}
            <div className="flex gap-2 flex-wrap">
              <span className="inline-flex items-center px-3 py-1 bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 text-xs font-medium rounded-full">
                <TrophyIcon className="w-3 h-3 mr-1" />
                DVG-Mitglied
              </span>
              <span className="inline-flex items-center px-3 py-1 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-xs font-medium rounded-full">
                <UserGroupIcon className="w-3 h-3 mr-1" />
                50+ Mitglieder
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4 lg:col-span-2">
            <h4 className="font-semibold text-neutral-900 dark:text-neutral-100">Navigation</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Sport Links */}
          <div className="space-y-4 lg:col-span-2">
            <h4 className="font-semibold text-neutral-900 dark:text-neutral-100">Sportarten</h4>
            <ul className="space-y-2">
              {sportLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Guest Training */}
          <div className="space-y-4 lg:col-span-2">
            <h4 className="font-semibold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
              <CalendarIcon className="w-4 h-4" />
              Gästetraining
            </h4>
            <div className="space-y-3">
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <span className="inline-block text-[11px] font-bold uppercase tracking-wide text-amber-800 dark:text-amber-300 bg-amber-200 dark:bg-amber-900/40 rounded-full px-2 py-0.5 mb-3">
                  Aktuell nur Warteliste
                </span>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Samstags</span>
                  <span className="text-xs text-neutral-600 dark:text-neutral-400">09:30 – 11:00</span>
                </div>
                <p className="text-xs text-neutral-600 dark:text-neutral-400">
                  Samstagsgruppe ausgebucht – Anmeldung für die Warteliste übers Kontaktformular.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="space-y-4 lg:col-span-2">
            <h4 className="font-semibold text-neutral-900 dark:text-neutral-100">Kontakt</h4>
            <ul className="space-y-3">
              <li>
                <a 
                  href={`mailto:${clubInfo.contact.email}`}
                  className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex items-center gap-2"
                >
                  <EnvelopeIcon className="w-4 h-4" />
                  <span className="truncate">{clubInfo.contact.email}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`tel:${clubInfo.contact.phone.replace(/[^0-9+]/g, '')}`}
                  className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex items-center gap-2"
                >
                  <PhoneIcon className="w-4 h-4" />
                  {clubInfo.contact.phone}
                </a>
              </li>
              <li>
                <div className="text-sm text-neutral-600 dark:text-neutral-400 flex items-start gap-2">
                  <MapPinIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p>Birkenweg</p>
                    <p>12439 Berlin</p>
                    <p className="text-xs mt-1 text-neutral-500">
                      Treptow-Köpenick
                    </p>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Additional Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mt-8 md:mt-12 pt-6 md:pt-8 border-t border-neutral-200 dark:border-neutral-700">
          <div className="flex items-start gap-3">
            <HomeIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5" />
            <div>
              <h5 className="font-medium text-sm text-neutral-900 dark:text-neutral-100">Vereinsgelände</h5>
              <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1">
                2 Trainingsplätze, Vereinsheim mit Aufenthaltsraum, Sanitäre Anlagen
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <AcademicCapIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5" />
            <div>
              <h5 className="font-medium text-sm text-neutral-900 dark:text-neutral-100">Qualifizierte Trainer</h5>
              <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1">
                5+ zertifizierte Trainer mit langjähriger Erfahrung
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <UserGroupIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5" />
            <div>
              <h5 className="font-medium text-sm text-neutral-900 dark:text-neutral-100">Gästetraining</h5>
              <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1">
                Samstags 09:30 – 11:00 • Aktuell nur Warteliste • Anmeldung übers Kontaktformular
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Legal Links Bar */}
      <div className="bg-neutral-50 dark:bg-neutral-900/50 border-t border-neutral-200 dark:border-neutral-700">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-wrap items-center justify-center gap-4">
            {legalLinks.map((link, index) => {
              const Icon = link.icon;
              return (
                <React.Fragment key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-xs text-neutral-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex items-center gap-1"
                  >
                    <Icon className="w-3 h-3" />
                    {link.name}
                  </Link>
                  {index < legalLinks.length - 1 && (
                    <span className="text-neutral-300 dark:text-neutral-700">•</span>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-neutral-200 dark:border-neutral-700">
        <div className="container mx-auto px-4 py-4 md:py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 md:gap-4">
            <p className="text-xs text-neutral-500 dark:text-neutral-500 text-center sm:text-left">
              © {currentYear} {clubInfo.name}. Alle Rechte vorbehalten.
            </p>
            <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-500">
              <span>Made with</span>
              <HeartIcon className="w-3 h-3 text-red-500 animate-pulse" />
              <span>for</span>
              <span className="text-primary-600 dark:text-primary-400">🐕</span>
              <span>in Berlin-Adlershof</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;